import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Shield, Play, ChevronDown, Info, Loader } from 'lucide-react'
import api from '../api'

const COMMON_PORTS = [
  { port: 21,    name: 'FTP',           desc: 'File Transfer Protocol',      color: '#60a5fa' },
  { port: 22,    name: 'SSH',           desc: 'Secure Shell – quản trị từ xa', color: '#34d399' },
  { port: 23,    name: 'Telnet',        desc: 'Telnet (không mã hóa)',         color: '#fbbf24' },
  { port: 25,    name: 'SMTP',          desc: 'Gửi email',                    color: '#60a5fa' },
  { port: 53,    name: 'DNS',           desc: 'Domain Name System',           color: '#60a5fa' },
  { port: 80,    name: 'HTTP',          desc: 'Web không mã hóa',             color: '#34d399' },
  { port: 110,   name: 'POP3',          desc: 'Nhận email (POP3)',            color: '#60a5fa' },
  { port: 143,   name: 'IMAP',          desc: 'Nhận email (IMAP)',            color: '#60a5fa' },
  { port: 443,   name: 'HTTPS',         desc: 'Web mã hóa SSL/TLS',           color: '#34d399' },
  { port: 445,   name: 'SMB',           desc: 'Windows File Sharing',         color: '#fbbf24' },
  { port: 465,   name: 'SMTPS',         desc: 'SMTP mã hóa SSL',              color: '#34d399' },
  { port: 587,   name: 'SMTP-TLS',      desc: 'SMTP mã hóa TLS',             color: '#34d399' },
  { port: 993,   name: 'IMAPS',         desc: 'IMAP mã hóa SSL',             color: '#34d399' },
  { port: 995,   name: 'POP3S',         desc: 'POP3 mã hóa SSL',             color: '#34d399' },
  { port: 1433,  name: 'MSSQL',         desc: 'Microsoft SQL Server',         color: '#f87171' },
  { port: 1521,  name: 'Oracle DB',     desc: 'Oracle Database',              color: '#f87171' },
  { port: 3306,  name: 'MySQL',         desc: 'MySQL / MariaDB',              color: '#f87171' },
  { port: 3389,  name: 'RDP',           desc: 'Remote Desktop (Windows)',     color: '#fbbf24' },
  { port: 5432,  name: 'PostgreSQL',    desc: 'PostgreSQL Database',          color: '#f87171' },
  { port: 5900,  name: 'VNC',           desc: 'Virtual Network Computing',    color: '#fbbf24' },
  { port: 6379,  name: 'Redis',         desc: 'Redis Cache Server',           color: '#f87171' },
  { port: 8080,  name: 'HTTP-Alt',      desc: 'HTTP thay thế / Proxy',        color: '#60a5fa' },
  { port: 8443,  name: 'HTTPS-Alt',     desc: 'HTTPS thay thế',              color: '#34d399' },
  { port: 9200,  name: 'Elasticsearch', desc: 'Elasticsearch REST API',       color: '#60a5fa' },
  { port: 27017, name: 'MongoDB',       desc: 'MongoDB Database',             color: '#f87171' },
]

function StatusBadge({ status }) {
  const map = {
    'open':          { bg: 'rgba(52,211,153,.15)', color: '#34d399', border: 'rgba(52,211,153,.4)', text: '🟢 MỞ (OPEN)' },
    'closed':        { bg: 'rgba(248,113,113,.15)', color: '#f87171', border: 'rgba(248,113,113,.4)', text: '🔴 ĐÓNG (CLOSED)' },
    'timeout':       { bg: 'rgba(251,191,36,.15)', color: '#fbbf24', border: 'rgba(251,191,36,.4)', text: '⏱ TIMEOUT' },
    'open|filtered': { bg: 'rgba(251,191,36,.15)', color: '#fbbf24', border: 'rgba(251,191,36,.4)', text: '🟡 MỞ/LỌC (UDP)' },
  }
  const s = map[status] || { bg: 'var(--panel)', color: 'var(--text2)', border: 'var(--border)', text: status }
  return (
    <span style={{
      padding: '3px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap',
    }}>
      {s.text}
    </span>
  )
}

export default function PortChecker({ wanIp }) {
  const [host, setHost] = useState('')
  const [port, setPort] = useState('80')
  const [protocol, setProtocol] = useState('tcp')
  const [timeoutMs, setTimeoutMs] = useState(5000)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [batchResults, setBatchResults] = useState([])
  const [batchRunning, setBatchRunning] = useState(false)
  const [showBatch, setShowBatch] = useState(false)
  const [batchProgress, setBatchProgress] = useState(0)

  useEffect(() => {
    if (wanIp && !host) setHost(wanIp)
  }, [wanIp])

  const checkPort = async () => {
    const h = host.trim()
    const p = parseInt(port)
    if (!h) return toast.error('Vui lòng nhập Host/IP hoặc tên miền')
    if (!p || p < 1 || p > 65535) return toast.error('Port phải từ 1 đến 65535')
    setLoading(true)
    setResult(null)
    try {
      const res = await api.post('/check-port', { host: h, port: p, protocol, timeout: timeoutMs })
      setResult(res.data)
    } catch (e) {
      toast.error('Lỗi kết nối tới API server: ' + (e.message || ''))
    } finally { setLoading(false) }
  }

  const scanBatch = async () => {
    const h = host.trim()
    if (!h) return toast.error('Vui lòng nhập Host/IP trước khi quét')
    setBatchRunning(true)
    setBatchResults([])
    setBatchProgress(0)
    const results = []
    for (let i = 0; i < COMMON_PORTS.length; i++) {
      const p = COMMON_PORTS[i]
      try {
        const res = await api.post('/check-port', { host: h, port: p.port, protocol: 'tcp', timeout: 3000 })
        results.push({ ...p, ...res.data })
      } catch {
        results.push({ ...p, status: 'error', latency: null })
      }
      setBatchResults([...results])
      setBatchProgress(Math.round(((i + 1) / COMMON_PORTS.length) * 100))
    }
    setBatchRunning(false)
    const openCount = results.filter(r => r.status === 'open').length
    toast.success(`Quét xong! ${openCount} port đang mở.`)
  }

  const resultStyle = result ? {
    open:          { bg: 'rgba(52,211,153,.07)',  border: 'rgba(52,211,153,.3)',  accent: '#34d399' },
    closed:        { bg: 'rgba(248,113,113,.07)', border: 'rgba(248,113,113,.3)', accent: '#f87171' },
    'open|filtered': { bg: 'rgba(251,191,36,.07)', border: 'rgba(251,191,36,.3)', accent: '#fbbf24' },
  }[result.status] || { bg: 'var(--panel)', border: 'var(--border)', accent: 'var(--text2)' } : null

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Title */}
      <div className="flex items-center gap-3">
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, var(--accent2), #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 18px rgba(56,189,248,.35)' }}>
          <Shield size={20} color="#000" />
        </div>
        <div>
          <h1 className="font-display" style={{ fontSize: 18 }}>Kiểm tra Port</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)' }}>Kiểm tra trạng thái TCP / UDP port của bất kỳ host nào</p>
        </div>
      </div>

      {/* Main form */}
      <div className="grad-border p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>IP / Tên miền</label>
            <input className="inp" placeholder="192.168.1.1 hoặc example.com"
              value={host} onChange={e => setHost(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && checkPort()} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>Port (1–65535)</label>
            <input className="inp" type="number" min="1" max="65535" placeholder="80"
              value={port} onChange={e => setPort(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && checkPort()} />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>Giao thức</label>
            <select className="inp" value={protocol} onChange={e => setProtocol(e.target.value)}>
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>Timeout</label>
            <select className="inp" value={timeoutMs} onChange={e => setTimeoutMs(parseInt(e.target.value))}>
              <option value={2000}>2 giây</option>
              <option value={5000}>5 giây</option>
              <option value={10000}>10 giây</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={checkPort} disabled={loading}>
              {loading
                ? <><Loader size={14} className="animate-spin" /> Đang kiểm tra...</>
                : <><Play size={14} /> Kiểm tra</>
              }
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => setShowBatch(v => !v)} disabled={batchRunning}>
              <ChevronDown size={14} style={{ transform: showBatch ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }} />
              Quét hàng loạt
            </button>
          </div>
        </div>

        {/* Result */}
        {result && resultStyle && (
          <div className="fade-in" style={{ padding: 16, borderRadius: 10, border: `1px solid ${resultStyle.border}`, background: resultStyle.bg }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <StatusBadge status={result.status} />
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: 'var(--text2)' }}>
                  {result.protocol?.toUpperCase()} · {result.host}:{result.port}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Độ trễ</div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700, color: resultStyle.accent }}>
                  {result.latency !== null && result.latency !== undefined ? `${result.latency}ms` : '—'}
                </div>
              </div>
            </div>
            {result.reason && (
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Info size={12} /> {result.reason}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Batch scan */}
      {showBatch && (
        <div className="fade-in grad-border p-5">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 className="font-display" style={{ fontSize: 15 }}>Quét các port phổ biến</h3>
              <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
                Quét {COMMON_PORTS.length} port thông dụng bằng TCP
              </p>
            </div>
            <button className="btn btn-primary" onClick={scanBatch} disabled={batchRunning}>
              {batchRunning
                ? <><Loader size={13} className="animate-spin" /> Đang quét {batchProgress}%</>
                : <><Play size={13} /> Bắt đầu quét</>
              }
            </button>
          </div>

          {batchRunning && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${batchProgress}%`, background: 'linear-gradient(to right, var(--accent2), var(--accent3))', transition: 'width .2s' }} />
              </div>
            </div>
          )}

          {batchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto' }}>
              {batchResults.map(r => (
                <div key={r.port} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: 8,
                  background: r.status === 'open' ? 'rgba(52,211,153,.05)' : 'var(--bg)',
                  border: r.status === 'open' ? '1px solid rgba(52,211,153,.25)' : '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: r.status === 'open' ? '#34d399' : '#f87171' }} />
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 700, color: r.color || 'var(--accent)', minWidth: 50 }}>{r.port}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{r.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }} className="md:block hidden">{r.desc}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {r.latency != null && (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text3)' }}>{r.latency}ms</span>
                    )}
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Common ports reference */}
      <div className="grad-border p-5">
        <h3 className="font-display" style={{ fontSize: 15, marginBottom: 4 }}>📋 Danh sách port thông dụng</h3>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Click vào port để điền nhanh vào ô kiểm tra</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {COMMON_PORTS.map(p => (
            <button key={p.port}
              onClick={() => setPort(String(p.port))}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)',
                cursor: 'pointer', textAlign: 'left', transition: 'all .18s', width: '100%',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'rgba(56,189,248,.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 56, padding: '3px 8px', borderRadius: 6,
                fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 700,
                color: p.color,
                background: p.color + '18',
                border: `1px solid ${p.color}40`,
                flexShrink: 0,
              }}>
                {p.port}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
